import { date as fmtDate } from '@orgazone/date-format';
import { useCallback, useEffect, useState } from 'react';
import { useLatest } from '@orgazone/ui-web';
import { api, hhmmss, type ApiTransportDetail, type ApiTransportListItem, type ApiTransportTest } from '../api';
import { Icon } from '../components/icons';
import { Modal } from '../components/Modal';
import { ServerIdentityBlock } from '../components/ServerIdentity';
import { Btn, Card, DataGate, Dot, PageHead } from '../components/ui';
import { useApp } from '../state';
import { TRANSPORT_CATALOG } from '../registry';
import { useResource } from '../useResource';

// Transports — vue CROSS-FOURNISSEUR des comptes de transport réellement configurés (§11). Chaque compte
// pend d'un fournisseur (créé depuis sa fiche) ; ici on les voit tous, on ouvre le DÉTAIL (config affichable
// SANS credentials §13, clients servis), on TESTE la connexion (vraie sonde) et on met en pause/réactive.
// Sous la liste : le catalogue des protocoles offerts (référence plateforme, non éditable).

type Toast = (m: string, t?: 'ok' | 'warn' | 'err' | 'info') => void;

export function Transports() {
  const { showToast, guard } = useApp();
  const [selId, setSelId] = useState<string | null>(null);

  // Jamais « empty » : la moitié basse (catalogue des protocoles) reste pertinente sans aucun compte —
  // le vide des comptes s'affiche inline dans sa section.
  const { state, data, reload, error } = useResource((signal) => api.get<ApiTransportListItem[]>('/admin/transports', signal));
  const list = data ?? [];

  // ⚠️ TITRE DE PAGE AJOUTÉ (relevé de la maquette), et sa seconde phrase compte autant que la
  // première : « un compte se crée depuis la fiche d'un fournisseur, jamais ici » est la règle que
  // l'écran doit dire, sans quoi on y cherche un bouton de création qui n'existe pas.
  const pageHead = (
    <PageHead title="Catalogue des transports" sub="Les comptes réellement configurés, puis les moyens d'acheminement pris en charge par la plateforme. Un compte se crée depuis la fiche d'un fournisseur, jamais ici." />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>
      {/* Comptes réels */}
      <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-800)', margin: '2px 2px 10px' }}>Comptes configurés · {list.length} compte(s)</div>
      {list.length === 0 && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', margin: '0 2px' }}>Aucun compte de transport — ils se créent depuis la fiche d'un fournisseur (écran Fournisseurs).</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {list.map((t) => (
          <button key={t.id} type="button" onClick={() => setSelId(t.id)}
            style={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'block' }}>
            <Card style={{ padding: 14, height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="swap" size={18} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>{t.type}</div>
                  <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.supplierName ?? '— fournisseur inconnu'}</div>
                </div>
                <Dot kind={t.active ? 'ok' : 'off'} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                {/* ⚠️ LE RYTHME NE SE DIT PLUS ICI, et c'est un retrait de MENSONGE : cette ligne annonçait
                    « Relève : défaut » ou une fenêtre PAR COMPTE, alors qu'aucun code ne lisait cette
                    fenêtre — un rythme par compte n'a jamais existé. Il vit désormais sur le FOURNISSEUR
                    (docs/CADRAGE-Planification-Par-Fournisseur.md), et c'est sa fiche qui le montre. */}
                <span>{t.active ? 'Actif' : 'En pause'}</span>
              </div>
            </Card>
          </button>
        ))}
      </div>

      {/* Catalogue des protocoles (référence plateforme) */}
      <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-800)', margin: '22px 2px 10px' }}>Protocoles offerts</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        {TRANSPORT_CATALOG.map((t) => (
          <Card key={t.key} style={{ padding: 16, opacity: t.available ? 1 : 0.75 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-500)', display: 'grid', placeItems: 'center' }}><Icon name="swap" size={16} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-800)' }}>{t.label}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t.dirs}</div></div>
              {/* Disponibilité RÉELLE du moteur (§23.0) — pas de protocole affiché comme offert s'il ne l'est pas. */}
              <span style={{ fontSize: 'var(--t-sub)', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--r-pill)', background: t.available ? 'var(--ok-bg)' : 'var(--ink-100)', color: t.available ? 'var(--ok-fg)' : 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t.available ? 'disponible' : 'à venir'}</span>
            </div>
            <RefRow label="Réglé au fournisseur" value={t.supplierParams} />
            <RefRow label="Réglé au client" value={t.clientParams} />
          </Card>
        ))}
      </div>

      {selId && <TransportDetailModal id={selId} onClose={() => setSelId(null)} onChanged={reload} showToast={showToast} guard={guard} />}
    </DataGate>
  );
}

function RefRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>{label}</div>
      <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-700)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

// Modale détail — charge le compte RÉEL : config affichable (sans credentials), clients servis, test, pause.
function TransportDetailModal({ id, onClose, onChanged, showToast, guard }: {
  id: string; onClose: () => void; onChanged: () => void | Promise<void>; showToast: Toast; guard: () => boolean;
}) {
  const [d, setD] = useState<ApiTransportDetail | null>(null);
  const [failed, setFailed] = useState(false);
  // ⚠️ Le test rend un verdict PAR DOSSIER : en mode per-client il sonde la racine PUIS chaque dossier
  //    client, et l'écran doit nommer ceux qui manquent — un « échec » global ne permet aucun geste.
  const [test, setTest] = useState<{ busy?: boolean; ok?: boolean; error?: string; dossiers?: ApiTransportTest['dossiers'] } | null>(null);
  const [busyPause, setBusyPause] = useState(false);

  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : la relecture qui suit une mise en pause croise celle de l'ouverture —
  //    revenue la dernière, l'ancienne réafficherait le compte « actif » et le bouton offrirait de le remettre en pause.
  const latest = useLatest();
  const load = useCallback(async () => {
    const isLatest = latest();
    setFailed(false);
    try {
      const detail = await api.transportDetail(id);
      if (!isLatest()) return;
      setD(detail);
    } catch {
      if (!isLatest()) return;
      setFailed(true);
    }
  }, [id, latest]);
  useEffect(() => { void load(); }, [load]);

  const runTest = async () => {
    if (!guard()) return; // sonde serveur réelle → gardée comme toute mutation
    setTest({ busy: true });
    try {
      const r = await api.testTransport(id);
      setTest({ ok: r.ok, error: r.error, dossiers: r.dossiers });
      // Un refus d'identité vient d'être RELEVÉ en base : relire le détail fait apparaître l'empreinte à confirmer.
      if (r.serverIdentity) await load();
      showToast(r.ok ? 'Connexion établie' : 'Échec : ' + (r.error ?? 'inconnu'), r.ok ? 'ok' : 'err');
    } catch (e) {
      setTest({ ok: false, error: (e as Error).message });
      showToast('Test impossible : ' + (e as Error).message, 'err');
    }
  };

  const togglePause = async () => {
    if (!d || !guard()) return;
    setBusyPause(true);
    try {
      await api.setTransportActive(id, !d.transport.active);
      showToast(d.transport.active ? 'Transport mis en pause' : 'Transport réactivé', 'ok');
      await load();
      await onChanged();
    } catch (e) {
      showToast('Échec : ' + (e as Error).message, 'err');
    } finally {
      setBusyPause(false);
    }
  };

  const t = d?.transport;
  const cfg = d?.config;
  return (
    <Modal title={t ? (t.supplierName ?? 'Transport') : 'Transport'} sub={t ? `${t.type.toUpperCase()} · ${t.active ? 'actif' : 'en pause'}` : undefined} icon="swap" onClose={onClose} wide
      footer={t ? (
        <>
          <Btn variant="ghost" mutating={false} onClick={onClose}>Fermer</Btn>
          <Btn variant={t.active ? 'ghost' : 'primary'} icon={t.active ? 'pause' : 'play'} disabled={busyPause} onClick={() => void togglePause()}>{t.active ? 'Mettre en pause' : 'Réactiver'}</Btn>
          <Btn icon="refresh" mutating={false} disabled={test?.busy} onClick={() => void runTest()}>{test?.busy ? 'Test…' : 'Tester la connexion'}</Btn>
        </>
      ) : undefined}>
      {failed && <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>Détail indisponible.</div>}
      {!failed && !d && <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Chargement…</div>}
      {d && t && (
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Verdict du dernier test */}
          {test?.ok === true && (
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ok-fg)', background: 'var(--ok-bg)', borderRadius: 'var(--r-xs)', padding: '8px 12px' }}>
              Connexion établie{test.dossiers?.length ? ` — ${test.dossiers.length} dossier(s) atteint(s).` : '.'}
            </div>
          )}
          {/* ⚠️ 🔴 LE DÉTAIL PAR DOSSIER EST LA RÉPONSE À UN INCIDENT RÉEL : un « 550 Directory not found. »
              nu ne disait pas DE QUEL CLIENT il parlait, et l'opérateur devait deviner lequel des liens du
              fournisseur manquait de son dossier sur le FTP. Chaque ligne nomme son client et montre le
              chemin exact à créer — et on ne crée rien à sa place (décision utilisateur). */}
          {test?.ok === false && (
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', background: 'var(--rose-bg)', borderRadius: 'var(--r-xs)', padding: '8px 12px', wordBreak: 'break-word', display: 'grid', gap: 7 }}>
              <div style={{ fontWeight: 600 }}>{test.error}</div>
              {(test.dossiers ?? []).filter((x) => !x.ok).map((x) => (
                <div key={x.dossier} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{x.client}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)' }}>{x.dossier}</span>
                  <span style={{ color: 'var(--ink-600)' }}>{x.error}</span>
                </div>
              ))}
              {(test.dossiers ?? []).some((x) => x.ok) && (
                <div style={{ color: 'var(--ink-500)', fontSize: 'var(--t-meta)' }}>
                  {(test.dossiers ?? []).filter((x) => x.ok).length} autre(s) dossier(s) répondent normalement — le dossier manquant est à créer sur le serveur du fournisseur.
                </div>
              )}
            </div>
          )}

          {/* Identité du serveur (SFTP, FTPS) : l'empreinte confirmée, celle à confirmer, et les gestes. */}
          {d.serverIdentity && <ServerIdentityBlock transportId={id} identity={d.serverIdentity} onChanged={async () => { setTest(null); await load(); }} />}

          {/* Config affichable (SANS credentials §13) */}
          <section>
            <div style={{ fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 }}>Connexion</div>
            {/* La CAUSE réelle, nommée par le serveur : clé absente, rien d'enregistré, ou bloc illisible (§23.0). */}
            {d && !cfg && <div role="status" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{d.configIssue?.detail ?? 'Configuration non lue — le serveur n’en a pas dit la cause.'}</div>}
            {cfg && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                <Field label="Protocole" value={cfg.protocol.toUpperCase()} mono />
                <Field label="Hôte" value={cfg.host ?? '—'} mono />
                <Field label="Port" value={cfg.port != null ? String(cfg.port) : '—'} mono />
                <Field label="Utilisateur" value={cfg.user ?? '—'} mono />
                {cfg.tls && <Field label="TLS" value={cfg.tls === 'implicit' ? 'implicite (port 990)' : 'explicite (AUTH TLS)'} />}
              </div>
            )}
            <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 6 }}>Le mot de passe / la clé ne sont jamais réaffichés (§13).</div>
          </section>

          {/* Layout (organisation des dossiers §5.1) */}
          {cfg && (
            <section>
              <div style={{ fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 }}>Dossiers (§5.1)</div>
              {cfg.layout.mode === 'shared-io' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                  <Field label="Mode" value="IN / OUT partagés" />
                  <Field label="Entrant" value={cfg.layout.inboundDir ?? '—'} mono />
                  <Field label="Sortant" value={cfg.layout.outboundDir ?? '—'} mono />
                  <Field label="Archive" value={cfg.layout.archiveDir ?? 'sinon supprimé'} mono />
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                  <Field label="Mode" value="Dossier par client" />
                  <Field label="Racine" value={cfg.layout.rootDir ?? '—'} mono />
                  <Field label="Archive" value={cfg.layout.archiveDir ?? 'sinon supprimé'} mono />
                </div>
              )}
            </section>
          )}

          {/* Clients servis (liens du fournisseur §4/§5.1) */}
          <section>
            <div style={{ fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 }}>Clients servis ({d.links.length})</div>
            {d.links.length === 0 && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>Aucun lien — associez ce fournisseur à des magasins dans l'écran Clients.</div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {d.links.map((l) => (
                <div key={l.gln} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--t-dense)', padding: '7px 10px', background: 'var(--ink-50)', borderRadius: 'var(--r-xs)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>{l.gln}</span>
                  {(l.folderName || l.inDir || l.outDir) && (
                    <span style={{ color: 'var(--ink-500)', fontSize: 'var(--t-meta)' }}>
                      {l.folderName ? `dossier ${l.folderName}` : ''}{l.inDir ? ` · in ${l.inDir}` : ''}{l.outDir ? ` · out ${l.outDir}` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>Créé le {fmtDate(t.createdAt)} à {hhmmss(t.createdAt)}</div>
        </div>
      )}
    </Modal>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>{label}</div>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)', marginTop: 2, wordBreak: 'break-word', fontFamily: mono ? 'var(--font-mono)' : undefined }}>{value}</div>
    </div>
  );
}
